// Phase 6 T35 — engine-telemetry Stimulus controller.
//
// Body-mounted singleton controller (data-controller="engine-telemetry" on
// <body> in app/views/layouts/application.html.erb). Single instance per
// page mount — per-element mounting would defeat batching and double-fire
// connect() on every Turbo navigation. [v1.1 FIX #16]
//
// Lifecycle / queue persistence:
//   The queue is in-memory only (this.queue, initialized fresh in connect()).
//   Turbo Drive replaces <body> on navigation, which tears down this
//   controller and resets its queue. To avoid losing pending events:
//     (a) `turbo:before-visit` listener flushes the queue FIRST so events
//         queued just before a click still ship via navigator.sendBeacon.
//     (b) `beforeunload` listener performs the equivalent flush for full
//         reloads / tab close (covers non-Turbo navigation paths).
//     (c) disconnect() also calls flush() as a final safety net when the
//         controller is torn down for any other reason.
//   There is NO cross-navigation persistence — each mount starts with an
//   empty queue. Do not assume events survive a navigation if a flush
//   listener somehow fails to fire.
//
// Markup contract:
//   <a data-action="click->engine-telemetry#emit"
//      data-engine-event="report.tab_switched"
//      data-engine-properties='{"from_tab":"overview","to_tab":"geography"}'>...</a>
//
// For text inputs (debounced):
//   <input data-action="input->engine-telemetry#emitDebounced"
//          data-engine-event="roi_calculator.input_changed"
//          data-engine-properties='{"field_name":"total_cost"}'>
//
// For partials that need to fire on render (no native action), use the
// engine-empty-state-emitter companion controller — it dispatches an
// `engine-telemetry:emit` window event in connect() that this controller
// listens for. Direct-mounting `engine-telemetry` on a partial would create
// a duplicate body-controller instance. [v1.1 FIX #18]
//
// Transport:
//   * navigator.sendBeacon when available (survives page unload)
//   * fetch with keepalive: true as fallback (older Safari)
//   * CSRF token read from <meta name="csrf-token"> in connect() — sendBeacon
//     can't set headers, but the fetch fallback uses X-CSRF-Token. The
//     /engine_events controller skips CSRF anyway (sendBeacon limitation).
//     [v1.1 FIX #17]
//
// Manual smoke procedure (no JS unit specs in this task — see T37 runbook):
//   1. Click a Reports Hub tab → Network tab shows POST /engine_events
//      with `report.tab_switched` and {from_tab, to_tab} properties.
//   2. Type into an ROI input, pause >400ms → POST with
//      `roi_calculator.input_changed` and {field_name}.
//   3. Land on an empty-state surface (e.g. Compare with 0 events) →
//      POST with `report.empty_state_shown`.
//   4. Apply a filter, then click "Clear filters" within 30s → POST with
//      `report.filter_cleared_within_30s` and {ms_since_apply}.
import { Controller } from "@hotwired/stimulus"

const BATCH_FLUSH_MS = 2000
const BATCH_MAX      = 25
const DEBOUNCE_MS    = 400
const ENDPOINT       = "/engine_events"
const FILTER_PAIR_WINDOW_MS = 30_000

export default class extends Controller {
  connect() {
    this.queue          = []
    this.debounceTimers = new Map()
    this.csrfToken      =
      document.querySelector('meta[name="csrf-token"]')?.content || ""

    // Bound method refs so removeEventListener can match on disconnect.
    this._boundFlush          = this.flush.bind(this)
    this._boundReceiveEmit    = this.receiveEmitEvent.bind(this)

    this.flushTimer = setInterval(this._boundFlush, BATCH_FLUSH_MS)

    window.addEventListener("beforeunload",         this._boundFlush)
    window.addEventListener("turbo:before-visit",   this._boundFlush)
    window.addEventListener("engine-telemetry:emit", this._boundReceiveEmit)
  }

  disconnect() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer)
      this.flushTimer = null
    }
    window.removeEventListener("beforeunload",         this._boundFlush)
    window.removeEventListener("turbo:before-visit",   this._boundFlush)
    window.removeEventListener("engine-telemetry:emit", this._boundReceiveEmit)

    // Clear any pending debounces so we don't enqueue after teardown.
    this.debounceTimers.forEach((id) => clearTimeout(id))
    this.debounceTimers.clear()

    // One last flush on the way out.
    this.flush()
  }

  // -----------------------------------------------------------------------
  // Action handlers
  // -----------------------------------------------------------------------

  // Standard action: read data-engine-event + data-engine-properties off the
  // currentTarget and enqueue. Fires synchronously — used for clicks + form
  // submits where there's no benefit to debouncing.
  emit(event) {
    const el        = event.currentTarget
    const eventType = el.dataset.engineEvent
    if (!eventType) return

    const properties = this._parseProperties(el)
    this._handleSpecialPairs(eventType, properties)

    // `report.filter_cleared` is a client-only marker, NOT in the catalog.
    // We only enqueue the derived `report.filter_cleared_within_30s`
    // (handled in _handleSpecialPairs). Bail before pushing.
    if (eventType === "report.filter_cleared") return

    this._enqueue({ event_type: eventType, properties })
  }

  // Debounced variant for text inputs — coalesces a burst of keystrokes
  // into a single event ~400ms after the last input. Keyed by eventType
  // so two unrelated debounced inputs don't share a timer.
  emitDebounced(event) {
    const el        = event.currentTarget
    const eventType = el.dataset.engineEvent
    if (!eventType) return

    const properties = this._parseProperties(el)
    const key = eventType

    if (this.debounceTimers.has(key)) {
      clearTimeout(this.debounceTimers.get(key))
    }
    this.debounceTimers.set(
      key,
      setTimeout(() => {
        this._enqueue({ event_type: eventType, properties })
        this.debounceTimers.delete(key)
      }, DEBOUNCE_MS)
    )
  }

  // Window-event bridge for partials that can't bind a Stimulus action
  // directly (see engine-empty-state-emitter). Reads the same shape from
  // event.detail — { eventType, properties }.
  receiveEmitEvent(event) {
    const detail     = event.detail || {}
    const eventType  = detail.eventType
    if (!eventType) return

    const properties = detail.properties || {}
    this._enqueue({ event_type: eventType, properties })
  }

  // -----------------------------------------------------------------------
  // Filter apply→clear pairing (Step 8)
  //
  // When `report.filter_applied` fires we stash a per-surface timestamp in
  // sessionStorage. When `report.filter_cleared` fires (a CLIENT-ONLY marker —
  // NOT a catalog event), we look up the stash, derive ms_since_apply, and
  // enqueue `report.filter_cleared_within_30s` ONLY if ms ≤ 30000. Wrapped
  // in try/catch because Safari Private Mode rejects sessionStorage writes.
  // -----------------------------------------------------------------------
  _handleSpecialPairs(eventType, properties) {
    const surface = properties.surface || "unknown"
    const key     = `engine_filter_applied_at:${surface}`

    try {
      if (eventType === "report.filter_applied") {
        sessionStorage.setItem(key, String(Date.now()))
        return
      }

      if (eventType === "report.filter_cleared") {
        const appliedAt = parseInt(sessionStorage.getItem(key) || "0", 10)
        sessionStorage.removeItem(key)
        if (appliedAt > 0) {
          const msSinceApply = Date.now() - appliedAt
          if (msSinceApply <= FILTER_PAIR_WINDOW_MS) {
            this._enqueue({
              event_type: "report.filter_cleared_within_30s",
              properties: { ...properties, ms_since_apply: msSinceApply }
            })
          }
        }
      }
    } catch (_e) {
      // sessionStorage unavailable (private mode, disabled storage). Swallow.
    }
  }

  // -----------------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------------

  _parseProperties(el) {
    const raw = el.dataset.engineProperties
    if (!raw) return {}
    try {
      const parsed = JSON.parse(raw)
      return typeof parsed === "object" && parsed !== null ? parsed : {}
    } catch (_e) {
      return {}
    }
  }

  _enqueue(payload) {
    this.queue.push(payload)
    if (this.queue.length >= BATCH_MAX) {
      this.flush()
    }
  }

  flush() {
    if (!this.queue.length) return

    const batch = this.queue.splice(0, this.queue.length)
    const body  = JSON.stringify({ events: batch })

    // sendBeacon is fire-and-forget and survives page unload — preferred.
    // It can't set headers, but the controller skips CSRF for that reason.
    if (navigator.sendBeacon) {
      try {
        const blob = new Blob([body], { type: "application/json" })
        const ok   = navigator.sendBeacon(ENDPOINT, blob)
        if (ok) return
      } catch (_e) {
        // Fall through to fetch fallback.
      }
    }

    // Fallback for older Safari and any sendBeacon failure.
    try {
      fetch(ENDPOINT, {
        method:      "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept":       "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body,
        keepalive:   true,
        credentials: "same-origin"
      }).catch(() => { /* swallow — telemetry is best-effort */ })
    } catch (_e) {
      // Last-resort swallow; never let telemetry crash the page.
    }
  }
}
